import { useEffect, useState } from "react";
import { Download, Calendar, FileText, Users, TrendingUp, Wrench, Briefcase, BarChart3 } from "lucide-react";
import api, { fileUrl } from "../lib/apiClient";
import { fmtCAD, fmtDate, todayIso } from "../lib/format";

const REPORTS = [
  { key: "hst", label: "HST Report (CRA)", icon: BarChart3, desc: "HST collected vs paid (ITCs), net owed" },
  { key: "pnl", label: "Profit & Loss", icon: TrendingUp, desc: "Revenue, COGS, gross & net profit" },
  { key: "corporate-tax", label: "Corporate Tax (Ontario)", icon: FileText, desc: "Estimated tax on net profit" },
  { key: "outstanding", label: "Outstanding Invoices", icon: FileText, desc: "Open invoices with aging" },
  { key: "customer-revenue", label: "Customer Revenue", icon: Users, desc: "Revenue per customer, ranked" },
  { key: "job-profitability", label: "Job Profitability", icon: Briefcase, desc: "Profit & margin per job" },
  { key: "employee-productivity", label: "Employee Productivity", icon: Users, desc: "Hours & revenue per employee" },
  { key: "expenses", label: "Expense Report", icon: Wrench, desc: "Expenses by category" },
  { key: "materials", label: "Materials Usage", icon: Wrench, desc: "Materials used across jobs" },
  { key: "revenue-by-type", label: "Revenue by Service Type", icon: BarChart3, desc: "Revenue grouped by job type" },
];

function periodPresets() {
  const t = new Date();
  const ms = (y, m, d = 1) => new Date(y, m, d).toISOString().slice(0, 10);
  const last = new Date(t); last.setMonth(t.getMonth() - 1);
  const q = Math.floor(t.getMonth() / 3);
  return {
    "This month": [ms(t.getFullYear(), t.getMonth()), ms(t.getFullYear(), t.getMonth() + 1).slice(0, 10)],
    "Last month": [ms(last.getFullYear(), last.getMonth()), ms(last.getFullYear(), last.getMonth() + 1)],
    "This quarter": [ms(t.getFullYear(), q * 3), ms(t.getFullYear(), q * 3 + 3)],
    "This year": [ms(t.getFullYear(), 0), ms(t.getFullYear(), 11, 31)],
  };
}

export default function Reports() {
  const [selected, setSelected] = useState("hst");
  const [range, setRange] = useState({ start: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10), end: todayIso() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const presets = periodPresets();

  const load = async () => {
    setLoading(true); setData(null);
    try {
      const { data } = await api.get(`/reports/${selected}?start=${range.start}&end=${range.end}`);
      setData(data);
    } catch (e) {
      setData({ error: e.response?.data?.detail || "Failed" });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [selected, range.start, range.end]);

  const exportUrl = fileUrl(`/reports/export.csv?report=${selected === "corporate-tax" ? "pnl" : selected === "revenue-by-service-type" ? "revenue-by-type" : selected}&start=${range.start}&end=${range.end}`);

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Financial &amp; operational reports — export to CSV.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3 space-y-1">
          {REPORTS.map((r) => (
            <button key={r.key} onClick={() => setSelected(r.key)} data-testid={`report-${r.key}`}
              className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors flex items-center gap-3 ${selected === r.key ? "bg-[#0F4C81] text-white" : "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"}`}>
              <r.icon size={16} strokeWidth={1.5} />
              <div className="flex-1"><div className="font-medium">{r.label}</div></div>
            </button>
          ))}
        </aside>

        <div className="lg:col-span-9 space-y-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-5 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="text-xs uppercase text-slate-500">Start</label>
                <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="block mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md" />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500">End</label>
                <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="block mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md" />
              </div>
              <div className="flex gap-1 flex-wrap">
                {Object.entries(presets).map(([k, v]) => (
                  <button key={k} onClick={() => setRange({ start: v[0], end: v[1] })} className="text-xs px-2 py-1 border border-slate-300 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800">{k}</button>
                ))}
              </div>
            </div>
            <a href={exportUrl} data-testid="export-report-btn" className="px-4 py-2 border border-slate-300 dark:border-slate-700 text-sm rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2">
              <Download size={14} /> Export CSV
            </a>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6">
            {loading && <div className="text-sm text-slate-500">Generating...</div>}
            {!loading && data && <ReportBody selected={selected} data={data} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatLabel({ label, value, accent }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display text-2xl font-bold tabular mt-1 ${accent || "text-slate-900 dark:text-white"}`}>{value}</div>
    </div>
  );
}

function ReportBody({ selected, data }) {
  if (data?.error) return <div className="text-sm text-red-600">{data.error}</div>;
  if (selected === "hst") return (
    <div className="space-y-4">
      <h3 className="font-display text-lg font-semibold">HST Summary</h3>
      <div className="grid grid-cols-3 gap-6">
        <StatLabel label="Collected (sales)" value={fmtCAD(data.hst_collected)} />
        <StatLabel label="Paid (ITCs)" value={fmtCAD(data.hst_paid_itcs)} />
        <StatLabel label="Net owed to CRA" value={fmtCAD(data.net_owed)} accent={data.net_owed >= 0 ? "text-[#0F4C81]" : "text-emerald-700"} />
      </div>
      <div className="border-t border-slate-200 dark:border-slate-700 pt-4 text-sm">
        <div className="text-xs uppercase text-slate-500 mb-2">Invoice breakdown</div>
        <table className="w-full text-sm tabular">
          <thead className="text-slate-500 text-xs uppercase"><tr><th className="text-left py-1">#</th><th className="text-left">Paid</th><th className="text-right">Amount</th><th className="text-right">HST share</th></tr></thead>
          <tbody>{data.invoice_breakdown.map((r, i) => <tr key={i} className="border-t border-slate-100 dark:border-slate-800"><td className="py-1 font-mono">{r.number}</td><td>{fmtDate(r.paid_date)}</td><td className="text-right">{fmtCAD(r.amount)}</td><td className="text-right">{fmtCAD(r.hst)}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
  if (selected === "pnl") {
    if (!data?.cogs) return <div className="text-sm text-slate-500">Loading...</div>;
    return (
    <div className="space-y-4">
      <h3 className="font-display text-lg font-semibold">Profit &amp; Loss</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
        <StatLabel label="Revenue" value={fmtCAD(data.revenue)} />
        <StatLabel label="COGS" value={fmtCAD(data.cogs.total)} />
        <StatLabel label="Gross Profit" value={fmtCAD(data.gross_profit)} accent="text-emerald-700" />
        <StatLabel label="Net Profit" value={fmtCAD(data.net_profit)} accent={data.net_profit >= 0 ? "text-emerald-700" : "text-red-600"} />
      </div>
      <table className="w-full text-sm tabular border-t border-slate-200 dark:border-slate-700 pt-4">
        <tbody>
          <tr><td className="py-2 pl-2">Revenue (cash basis)</td><td className="text-right py-2 pr-2 font-medium">{fmtCAD(data.revenue)}</td></tr>
          <tr><td className="py-2 pl-6 text-slate-600">– Materials cost</td><td className="text-right py-2 pr-2">{fmtCAD(data.cogs.materials)}</td></tr>
          <tr><td className="py-2 pl-6 text-slate-600">– Labour cost</td><td className="text-right py-2 pr-2">{fmtCAD(data.cogs.labour)}</td></tr>
          <tr className="border-t border-slate-200 dark:border-slate-700 font-semibold"><td className="py-2 pl-2">Gross Profit</td><td className="text-right py-2 pr-2">{fmtCAD(data.gross_profit)}</td></tr>
          {Object.entries(data.operating_expenses_by_category).map(([k, v]) => <tr key={k}><td className="py-2 pl-6 text-slate-600">– {k}</td><td className="text-right py-2 pr-2">{fmtCAD(v)}</td></tr>)}
          <tr className="border-t border-slate-200 dark:border-slate-700"><td className="py-2 pl-2">Operating expenses</td><td className="text-right py-2 pr-2 font-medium">{fmtCAD(data.total_operating_expenses)}</td></tr>
          <tr className="border-t-2 border-[#0F4C81] font-bold text-base"><td className="py-3 pl-2">Net Profit</td><td className={`text-right py-3 pr-2 ${data.net_profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtCAD(data.net_profit)}</td></tr>
        </tbody>
      </table>
    </div>
  );
  }
  if (selected === "corporate-tax") {
    if (data?.estimated_tax === undefined) return <div className="text-sm text-slate-500">Loading...</div>;
    return (
    <div className="space-y-4">
      <h3 className="font-display text-lg font-semibold">Ontario Corporate Tax Estimate</h3>
      <div className="grid grid-cols-3 gap-6">
        <StatLabel label="Net Profit" value={fmtCAD(data.net_profit)} />
        <StatLabel label="Tax Rate" value={`${data.tax_rate_percent}%`} />
        <StatLabel label="Estimated Tax" value={fmtCAD(data.estimated_tax)} accent="text-[#0F4C81]" />
      </div>
      <div className="text-xs bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-900 text-amber-900 dark:text-amber-200 rounded-md p-3">⚠️ {data.disclaimer}</div>
    </div>
  );
  }
  if (selected === "outstanding") {
    if (!data?.rows) return <div className="text-sm text-slate-500">Loading...</div>;
    return (
    <ReportTable
      title="Outstanding Invoices"
      summary={[["Total balance", fmtCAD(data.total_balance)]]}
      columns={["Number", "Customer", "Due", "Total", "Balance", "Days", "Bucket"]}
      rows={data.rows.map((r) => [r.number, r.customer, fmtDate(r.due_date), fmtCAD(r.total), fmtCAD(r.balance), r.days_overdue, r.bucket])} />
  );
  }
  if (selected === "customer-revenue") {
    if (!data?.rows) return <div className="text-sm text-slate-500">Loading...</div>;
    return (
    <ReportTable title="Revenue per Customer"
      summary={[["Total revenue", fmtCAD(data.total)]]}
      columns={["Customer", "Revenue"]}
      rows={data.rows.map((r) => [r.name, fmtCAD(r.revenue)])} />
  );
  }
  if (selected === "job-profitability") {
    if (!data?.rows) return <div className="text-sm text-slate-500">Loading...</div>;
    return (
    <ReportTable title="Job Profitability"
      summary={[["Jobs", data.rows.length]]}
      columns={["Job #", "Customer", "Revenue", "Cost", "Profit", "Margin"]}
      rows={data.rows.map((r) => [r.number, r.customer, fmtCAD(r.revenue), fmtCAD(r.cost), fmtCAD(r.profit), `${r.margin_percent}%`])} />
  );
  }
  if (selected === "employee-productivity") {
    if (!data?.rows) return <div className="text-sm text-slate-500">Loading...</div>;
    return (
    <ReportTable title="Employee Productivity"
      summary={[["Employees", data.rows.length]]}
      columns={["Employee", "Hours", "Revenue", "Jobs Assigned", "Completed", "Avg Job"]}
      rows={data.rows.map((r) => [r.name, `${r.hours}h`, fmtCAD(r.revenue_generated), r.jobs_assigned, r.jobs_completed, fmtCAD(r.average_job_size)])} />
  );
  }
  if (selected === "expenses") {
    if (!data?.by_category) return <div className="text-sm text-slate-500">Loading...</div>;
    return (
    <div>
      <h3 className="font-display text-lg font-semibold mb-3">Expenses by category</h3>
      <div className="text-sm mb-2">Total: <span className="font-semibold">{fmtCAD(data.total)}</span></div>
      <table className="w-full text-sm tabular">
        <thead className="text-slate-500 text-xs uppercase"><tr><th className="text-left py-2">Category</th><th className="text-right">Total</th></tr></thead>
        <tbody>{Object.entries(data.by_category).map(([k, v]) => <tr key={k} className="border-t border-slate-100 dark:border-slate-800"><td className="py-2">{k}</td><td className="text-right">{fmtCAD(v)}</td></tr>)}</tbody>
      </table>
    </div>
  );
  }
  if (selected === "materials") {
    if (!data?.rows) return <div className="text-sm text-slate-500">Loading...</div>;
    return (
    <ReportTable title="Materials Usage" summary={[]} columns={["Material", "Qty", "Cost", "Revenue", "Profit"]}
      rows={data.rows.map((r) => [r.name, r.quantity, fmtCAD(r.cost), fmtCAD(r.revenue), fmtCAD(r.profit)])} />
    );
  }
  if (selected === "revenue-by-service-type") {
    if (!data?.by_type) return <div className="text-sm text-slate-500">Loading...</div>;
    return (
    <div>
      <h3 className="font-display text-lg font-semibold mb-3">Revenue by Service Type</h3>
      <div className="text-sm mb-2">Total: <span className="font-semibold">{fmtCAD(data.total)}</span></div>
      <table className="w-full text-sm tabular"><tbody>
        {Object.entries(data.by_type).map(([k, v]) => <tr key={k} className="border-t border-slate-100 dark:border-slate-800"><td className="py-2">{k}</td><td className="text-right">{fmtCAD(v)}</td></tr>)}
      </tbody></table>
    </div>
  );
  }
  if (selected === "hst" && !data?.invoice_breakdown) return <div className="text-sm text-slate-500">Loading...</div>;
  return null;
}

function ReportTable({ title, summary, columns, rows }) {
  return (
    <div>
      <h3 className="font-display text-lg font-semibold mb-3">{title}</h3>
      <div className="flex gap-6 mb-4">
        {(summary || []).map(([k, v]) => <div key={k}><div className="text-xs uppercase text-slate-500">{k}</div><div className="font-display text-xl font-bold tabular">{v}</div></div>)}
      </div>
      <table className="w-full text-sm tabular">
        <thead className="text-slate-500 text-xs uppercase">
          <tr>{columns.map((c) => <th key={c} className="text-left py-2">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} className="text-center py-6 text-slate-500">No data.</td></tr>}
          {rows.map((row, i) => <tr key={i} className="border-t border-slate-100 dark:border-slate-800">{row.map((c, j) => <td key={j} className="py-2">{c}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}
